import { Box, Heading, ListItem, Stack, Text, UnorderedList } from '@chakra-ui/react'

import { getSATBootcampUrl } from '@/client/services/url'

import ShwLink from '@/components/ShwLink'

export const courseSlugToSubheader: Record<string, () => React.ReactElement> = {
  statistics: () => (
    <Box>
      <Text mb="16px">
        Struggling with statistics concepts? Don&apos;t panic. Get targeted help with free online
        tutoring at Schoolhouse, founded by Sal Khan of Khan Academy.
      </Text>
      <UnorderedList>
        <ListItem>
          Join small-group sessions led by peers who get it. Practice problems, ask questions, and
          build confidence.
        </ListItem>
        <ListItem>
          Flexible options: Need a quick review session? Want ongoing prep? We&apos;ve got you
          covered.
        </ListItem>
        <ListItem>
          Find a supportive community to help you stay motivated along the way. Don&apos;t tackle
          statistics alone.
        </ListItem>
      </UnorderedList>
    </Box>
  ),
  sat: () => (
    <Stack direction="column" spacing="16px">
      <Heading size="lg">
        Stop struggling with the SAT—get the free SAT tutoring, prep, and help you need to succeed!
      </Heading>
      <Text>
        Feeling lost, overwhelmed, and stressed when it comes to the SAT? We totally understand. The
        SAT can be a major roadblock, but it doesn&apos;t have to be! Here&apos;s why you should
        choose Schoolhouse for your SAT prep:
      </Text>
      <UnorderedList>
        <ListItem>
          <b>Expert guidance every step of the way:</b> Our trained and certified tutors break down
          complex concepts and give personalized advice where you need the most support.
        </ListItem>
        <ListItem>
          <b>Targeted practice to build your skills:</b> Pinpoint your weaknesses and tackle them
          head-on with practice questions and focused review sessions.
        </ListItem>
        <ListItem>
          <b>Strategies designed for test day success:</b> Learn proven techniques to manage time,
          tackle those tricky questions with confidence, and maximize your score.
        </ListItem>
        <ListItem>
          <b>A supportive community to keep you going:</b> Connect with other students, get your
          questions answered, and find the motivation to keep pushing forward.
        </ListItem>
        <ListItem>
          <b>Zero cost, unlimited potential:</b> Everything at Schoolhouse is completely free! We
          believe that high-quality tutoring should be available to everyone.
        </ListItem>
      </UnorderedList>
      <Text>
        Ready to ditch the SAT stress and start seeing results? Check out our upcoming sessions and
        sign up today! Let&apos;s crush this test together!{' '}
        <ShwLink href={getSATBootcampUrl()}>
          Click here to learn more about the Schoolhouse SAT Bootcamp program.
        </ShwLink>
      </Text>
    </Stack>
  ),
  algebra: () => (
    <Text>
      Looking for a free Algebra 1 tutor? Join <ShwLink href="/">schoolhouse.world</ShwLink>, a free
      tutoring platform started by Sal Khan of Khan Academy, where you can find online algebra
      tutoring alongside students across the world. Hop into a single tutoring session hosted by a
      volunteer Algebra 1 tutor, or register for a series: a sequence of algebra tutoring sessions
      with the same Algebra 1 tutor. Sign up today for free, Algebra 1 tutoring and a community
      that’ll support you every step of the way.
    </Text>
  ),
  algebra2: () => (
    <Box>
      <Text mb="16px">
        Feeling overwhelmed by polynomials and complex numbers? Equations leaving you frustrated?
        Don&apos;t worry, you&apos;re not alone! Our free Algebra 2 tutors understand those
        struggles, and they&apos;re ready to guide you towards clarity and confidence. Here&apos;s
        what you&apos;ll gain from Schoolhouse tutoring:
      </Text>
      <UnorderedList>
        <ListItem>
          <b>Step-by-step explanations that break down complex concepts into manageable pieces.</b>
        </ListItem>
        <ListItem>
          <b>Personalized support to address your individual problem areas.</b>
        </ListItem>
        <ListItem>
          <b>Strategies to boost your problem-solving skills for Algebra 2 and beyond.</b>
        </ListItem>
        <ListItem>
          <b>A supportive community of peers who understand what you&apos;re going through.</b>
        </ListItem>
        <ListItem>
          <b>The reassurance that comes from asking questions in a judgement-free zone.</b>
        </ListItem>
      </UnorderedList>
    </Box>
  ),
  geometry: () => (
    <Box>
      <Text mb="16px">
        Feeling lost in a world of angles, shapes, and proofs? Geometry can be overwhelming, but it
        doesn&apos;t have to be. Our experienced tutors understand those struggles, and they&apos;re
        here to guide you from confusion to clarity. Get ready to:
      </Text>
      <UnorderedList>
        <ListItem>
          <b>Master core geometry concepts for your grade level.</b>
        </ListItem>
        <ListItem>
          <b>Conquer tricky problems with step-by-step explanations.</b>
        </ListItem>
        <ListItem>
          <b>Build confidence to tackle geometry homework and exams.</b>
        </ListItem>
        <ListItem>
          <b>Discover that geometry can actually be fun and rewarding!</b>
        </ListItem>
      </UnorderedList>
    </Box>
  ),
  trigonometry: () => (
    <Text>
      Looking for free trigonometry help? Sign up for a free trigonometry tutoring session at{' '}
      <ShwLink href="/">schoolhouse.world</ShwLink>, a peer tutoring platform founded by Sal Khan of
      Khan Academy. We offer small-group trigonometry tutoring sessions to learn trigonometry with
      learners from round the world. Choose between one-off trigonometry tutoring sessions, or a
      series of sessions for long-term learning with our volunteer trig tutors. Join Schoolhouse to
      learn trigonometry with a community that&apos;ll support you!
    </Text>
  ),
}

export default function getCoursePageSubheader(
  courseSlug: string | null
): () => React.ReactElement {
  if (courseSlug == null || courseSlugToSubheader[courseSlug] == null) {
    return () => (
      <Text>
        Sign up for a session at <ShwLink href="/">schoolhouse.world</ShwLink>, a free peer tutoring
        platform founded by Sal Khan of Khan Academy. We offer virtual small-group sessions with
        volunteer tutors from around the world. Join Schoolhouse to learn with a community
        that&apos;ll support you!
      </Text>
    )
  }
  return courseSlugToSubheader[courseSlug]
}
